import styled from 'styled-components';
import { ColorNordicGrey4, Spacing64, Spacing8, Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import { breakpoint, sizes } from 'variables';
import { Typography } from 'components/generic/typography';
import type { IFeaturedFooter } from '../shared/types';
import ReadMoreLink from './ReadMoreLink';
import { handleClickTracking } from '../shared/tracking';
import PageActionButton from 'components/Button/PageActionButton';

const Wrapper = styled.div<{ background?: string }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${({ background }): string => background || ColorNordicGrey4};
    padding: ${Spacing32} ${Spacing16};

    ${breakpoint.up(sizes.xl)} {
        padding: ${Spacing64};
    }
`;

const ButtonList = styled.div`
    width: 100%;
    max-width: 500px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: center;
    gap: ${Spacing8};
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.xl)} {
        gap: ${Spacing16};
        grid-template-columns: repeat(auto-fit, minmax(160px, 210px));
        max-width: 900px;
        margin-bottom: ${Spacing32};
    }
`;

const StyledHeadline = styled(Typography.Headline)`
    && {
        margin-top: 0;
        margin-bottom: ${Spacing16};

        ${breakpoint.up(sizes.xl)} {
            margin-bottom: ${Spacing32};
        }
    }
`;

export default function FeaturedFooter({ model, customTheme }: IFeaturedFooter) {
    const { headline, links, showMoreLink } = model;
    const { Icon, background, buttonVariant = 'negative' } = customTheme || {};

    return (
        <Wrapper background={background}>
            {Icon && <Icon />}
            <StyledHeadline size="L">{headline}</StyledHeadline>

            <ButtonList>
                {links.map((link, index) => {
                    const { pageAction, text, datalayer } = link;

                    return (
                        <PageActionButton
                            key={index}
                            action={pageAction}
                            variant={buttonVariant}
                            size={{ xs: 'md', lg: 'lg' }}
                            onClick={() => text && handleClickTracking(text)}
                            data-datalayer={JSON.stringify(datalayer)}
                        >
                            {text}
                        </PageActionButton>
                    );
                })}
            </ButtonList>

            <ReadMoreLink {...showMoreLink} />
        </Wrapper>
    );
}
